// components/WhoIsThisPanel.tsx — G6 C1 (decisions dab35ce1, 2ba58688; guard fe2c4ddf): who is this?
//
// A call (and, later, a text) arrives with a number the door CLAIMS and nobody has proven. This
// panel is the person's answer: the customer who holds the number now (shown, never acted on),
// someone else, or someone new. Every call asks again (2ba58688) — the confirmed customer is simply
// the one pre-filled. Moving a number from one customer to another is said before it happens, and
// the receipt names it after.
import { useState } from 'preact/hooks';
import { ApiError, confirmWhoIsThis, listParties, type PartyApi, type WhoIsThisApi } from '../lib/api';
import './WhoIsThisPanel.css';

const PLATFORM_WORD: Record<string, string> = { instagram: 'Instagram', facebook_page: 'Facebook' };

/** +14165550123 → "+1 416-555-0123"; a DM handle `instagram:@ada` → "@ada · Instagram" (G6 C3);
 * anything else is shown as it came. */
export function formatClaimedNumber(value: string): string {
  const m = /^\+1(\d{3})(\d{3})(\d{4})$/.exec(value);
  if (m) return `+1 ${m[1]}-${m[2]}-${m[3]}`;
  const h = /^([a-z_]+):(@.+)$/.exec(value);
  if (h && PLATFORM_WORD[h[1]!]) return `${h[2]} · ${PLATFORM_WORD[h[1]!]}`;
  return value;
}

interface Props {
  tenantId: string;
  threadId: string;
  who: WhoIsThisApi;
  onAnswered: (next: WhoIsThisApi | null) => void;
}

type Pick = { kind: 'party'; partyId: string; name: string } | { kind: 'new'; name: string };

export function WhoIsThisPanel({ tenantId, threadId, who, onAnswered }: Props) {
  const [mode, setMode] = useState<'idle' | 'other' | 'new'>('idle');
  const [parties, setParties] = useState<PartyApi[] | null>(null);
  const [filter, setFilter] = useState('');
  const [newName, setNewName] = useState('');
  const [chosen, setChosen] = useState<Pick | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [receipt, setReceipt] = useState<string | null>(null);

  const number = formatClaimedNumber(who.value);
  const holder = who.suggestion;

  async function answer(pick: Pick) {
    setBusy(true);
    setError(null);
    try {
      const r = await confirmWhoIsThis(
        tenantId,
        threadId,
        pick.kind === 'party' ? { partyId: pick.partyId } : { newParty: { name: pick.name } }
      );
      setReceipt(
        r.movedFrom
          ? `filed under ${r.party.name} · ${number} moved from ${r.movedFrom.name}`
          : `filed under ${r.party.name}`
      );
      setMode('idle');
      setChosen(null);
      onAnswered(r.whoIsThis);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'the answer was not saved');
    } finally {
      setBusy(false);
    }
  }

  async function openOther() {
    setMode('other');
    setChosen(null);
    if (parties === null) {
      try {
        setParties(await listParties(tenantId));
      } catch (err) {
        setError(err instanceof ApiError ? err.message : 'the customer list could not be read');
      }
    }
  }

  const shown = (parties ?? [])
    .filter((p) => p.displayName.toLowerCase().includes(filter.trim().toLowerCase()))
    .slice(0, 12);
  const moves = chosen && holder && (chosen.kind === 'new' || chosen.partyId !== holder.partyId) ? holder : null;

  return (
    <section class="who" aria-label="who is this">
      <div class="who__line">
        <span class="who__number">{number}</span>
        <span class="who__state">
          {who.confirmed ? `confirmed as ${who.confirmed.name}` : 'not confirmed'}
        </span>
      </div>

      {receipt ? <div class="who__receipt">{receipt}</div> : null}
      {error ? <div class="who__error" role="alert">{error}</div> : null}

      {!who.confirmed || mode !== 'idle' ? (
        <div class="who__answers">
          {holder ? (
            <button type="button" class="who__btn who__btn--main" disabled={busy} onClick={() => void answer({ kind: 'party', ...holder })}>
              It’s {holder.name}
            </button>
          ) : null}
          <button type="button" class="who__btn" disabled={busy} onClick={() => void openOther()}>
            Someone else
          </button>
          <button type="button" class="who__btn" disabled={busy} onClick={() => { setMode('new'); setChosen(null); }}>
            New customer
          </button>
        </div>
      ) : (
        <div class="who__answers">
          <button type="button" class="who__btn" disabled={busy} onClick={() => void openOther()}>
            Change
          </button>
        </div>
      )}

      {mode === 'other' ? (
        <div class="who__pick">
          <input
            class="who__input"
            type="search"
            aria-label="customer name"
            placeholder="Name"
            value={filter}
            onInput={(e) => setFilter((e.target as HTMLInputElement).value)}
          />
          <div class="who__list">
            {shown.map((p) => (
              <button
                type="button"
                key={p.id}
                class={`who__option${chosen?.kind === 'party' && chosen.partyId === p.id ? ' who__option--on' : ''}`}
                onClick={() => setChosen({ kind: 'party', partyId: p.id, name: p.displayName })}
              >
                {p.displayName}
              </button>
            ))}
            {parties !== null && shown.length === 0 ? <span class="who__none">No customer by that name.</span> : null}
          </div>
        </div>
      ) : null}

      {mode === 'new' ? (
        <div class="who__pick">
          <input
            class="who__input"
            type="text"
            aria-label="new customer's name"
            placeholder="Name"
            maxLength={200}
            value={newName}
            onInput={(e) => {
              const v = (e.target as HTMLInputElement).value;
              setNewName(v);
              setChosen(v.trim() ? { kind: 'new', name: v.trim() } : null);
            }}
          />
        </div>
      ) : null}

      {chosen ? (
        <div class="who__confirm">
          {moves ? (
            <span class="who__moves">
              {number} is on {moves.name} now. Confirming moves it to {chosen.name}.
            </span>
          ) : null}
          <button type="button" class="who__btn who__btn--main" disabled={busy} onClick={() => void answer(chosen)}>
            {chosen.kind === 'new' ? `Add ${chosen.name}` : `It’s ${chosen.name}`}
          </button>
        </div>
      ) : null}
    </section>
  );
}
